import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MultiSectionLayout,
  InteractiveContainer,
  PageLoaderFull,
} from "@jugl-web/ui-components";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { cx } from "@jugl-web/utils/cx";
import {
  useRestApiProvider,
  SubscriptionGetDetailedResponse,
} from "@jugl-web/rest-api";
import useEntity from "@web-src/features/app/hooks/useEntity";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { usePlansData } from "@web-src/modules/cx/components/WorkspaceSubscriptionDrawer/hooks/usePlansData";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { PlanCardInfo } from "@web-src/modules/cx/components/PlanCardInfo";
import { SelectedPlanInfo, SubscriptionPeriod } from "./types";
import { SubscriptionSummary } from "./components/SubscriptionSummary";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export const SummaryStep: React.FC<{ planInfo: SelectedPlanInfo }> = ({
  planInfo,
}) => {
  const showAlert = useShowAlert();
  const { entity } = useEntity();
  const { subscriptionV2Api } = useRestApiProvider();
  const {
    updateCardDetails,
    hasPaymentMethod,
    closePurchaseDialog,
    openSubscriptionDrawer,
    upgradeSubscriptionPlan,
    currentPlan,
    isMonthly,
    isChangePlanInProgress,
  } = useSubscriptionV2();
  const { generateUrl } = useNavigation();
  const [isApplyingPlan, setIsApplyingPlan] = useState(false);

  const { data: plansData } = subscriptionV2Api.useListPlansQuery(
    entity ? { entityId: entity.id, params: { type: "cx" } } : skipToken
  );

  const { plansFeatures } = usePlansData(plansData?.data ?? []);

  const { data: subscriptionData } = subscriptionV2Api.useGetSubscriptionQuery(
    entity ? { entityId: entity.id, params: { mode: "detailed" } } : skipToken
  );

  const detailedSubscription = subscriptionData as
    | SubscriptionGetDetailedResponse
    | undefined;

  const hasExistingSubscription =
    !!detailedSubscription?.details && currentPlan?.meta_data.idx !== 0;

  // Find the target plan (first available plan from the API)
  const targetPlan = useMemo(() => {
    if (!plansData) return undefined;

    return (
      plansData.data.find((plan) => plan.id === planInfo.planId) ||
      plansData.data[0]
    );
  }, [plansData, planInfo]);

  const [subscriptionPeriod, setSubscriptionPeriod] =
    useState<SubscriptionPeriod>("year");

  useEffect(() => {
    if (planInfo) {
      setSubscriptionPeriod(planInfo.period === "month" ? "month" : "year");
    }
  }, [planInfo]);

  const selectedPlanFeatures = useMemo(
    () =>
      plansFeatures.find(
        (plan) =>
          plan.id === targetPlan?.id || plan.planName === targetPlan?.name
      )?.features ??
      plansFeatures[0]?.features ??
      [],
    [plansFeatures, targetPlan]
  );

  // Derive price from V2 API plan data
  const selectedPlanPrice = useMemo(
    () => targetPlan?.prices.find((p) => p.interval === subscriptionPeriod),
    [targetPlan, subscriptionPeriod]
  );
  const basePlanPrice = selectedPlanPrice?.unit_amount ?? 0;

  const { data: invoicePreview } = subscriptionV2Api.usePreviewInvoiceQuery(
    entity && selectedPlanPrice?.id
      ? { entityId: entity.id, params: { price_id: selectedPlanPrice.id } }
      : skipToken
  );
  const amountDue = invoicePreview?.amount_due ?? null;
  const summaryTotal = amountDue != null ? amountDue : basePlanPrice;
  const credits =
    amountDue != null && amountDue < basePlanPrice
      ? basePlanPrice - amountDue
      : undefined;
  const basePlanPeriodLabel =
    subscriptionPeriod === "year" ? "yearly" : "monthly";
  const planName = targetPlan?.name || "Free";
  const defaultSeats =
    targetPlan?.default_addons?.find((a) => a.id === "jugl::users")?.credits ??
    0;

  const yearlySummary = useMemo(() => {
    if (subscriptionPeriod !== "year" || !targetPlan) return null;
    const yearly = targetPlan.prices.find((p) => p.interval === "year");
    const monthly = targetPlan.prices.find((p) => p.interval === "month");
    if (!yearly || !monthly || monthly.unit_amount === 0) return null;
    const originalYearlyTotal = monthly.unit_amount * 12;
    if (originalYearlyTotal <= yearly.unit_amount) return null;
    return {
      originalPrice: originalYearlyTotal,
      monthlyEquivalentDollars: Math.round(yearly.unit_amount / 12 / 100),
    };
  }, [subscriptionPeriod, targetPlan]);

  const isDowngrade =
    hasExistingSubscription &&
    (currentPlan?.meta_data.idx ?? 0) > (targetPlan?.meta_data.idx ?? 0);

  const currentInterval: SubscriptionPeriod = isMonthly ? "month" : "year";
  const isSameTierAsCurrent =
    hasExistingSubscription && currentPlan?.id === targetPlan?.id;
  const isAlreadyOnSelectedPlan =
    isSameTierAsCurrent && currentInterval === subscriptionPeriod;

  const onPurchaseSuccess = useCallback(() => {
    setIsApplyingPlan(true);
    setTimeout(() => {
      if (hasExistingSubscription) {
        if (isDowngrade) {
          sessionStorage.setItem(
            "onboarding_flow_pending",
            JSON.stringify({
              flow: "planDowngraded",
              planName,
              previousPlanName: currentPlan?.name ?? "",
            })
          );
        } else {
          sessionStorage.setItem(
            "onboarding_flow_pending",
            JSON.stringify({ flow: "planUpgraded", planName })
          );
        }
        window.location.reload();
      } else {
        // New subscription — navigate to AI Agents + onboarding
        sessionStorage.setItem(
          "onboarding_flow_pending",
          JSON.stringify({ flow: "postPurchase" })
        );
        window.location.href = generateUrl("cxAIAgents");
      }
    }, 5000);
  }, [
    generateUrl,
    hasExistingSubscription,
    planName,
    isDowngrade,
    currentPlan?.name,
  ]);

  const handleCompletePurchase = useCallback(async () => {
    if (!entity || !selectedPlanPrice || !targetPlan?.id) return;
    if (isAlreadyOnSelectedPlan) return;

    // Upgrading existing plan — card must exist
    if (!hasPaymentMethod) {
      const result = await updateCardDetails();
      if (result.result !== "success") return;
    }
    try {
      await upgradeSubscriptionPlan({
        planId: targetPlan.id,
        priceId: selectedPlanPrice.id,
        planName: targetPlan.name,
        onSuccess: () => onPurchaseSuccess(),
      });
    } catch {
      // Provider shows payment failed alert
    }
  }, [
    entity,
    selectedPlanPrice,
    hasPaymentMethod,
    targetPlan,
    isAlreadyOnSelectedPlan,
    upgradeSubscriptionPlan,
    updateCardDetails,
    onPurchaseSuccess,
  ]);

  const handleIntervalChange = (period: SubscriptionPeriod) => {
    if (period === "month" && subscriptionPeriod === "year") {
      showAlert({
        title: "Save money with better deal",
        content: (
          <div className="text-dark-700">
            You can{" "}
            <span className="bg-gradient-to-r from-[#1E82D2] to-[#A528AB] bg-clip-text text-transparent">
              save up to 18%
            </span>{" "}
            by paying Annually. Are you sure you want to switch to Monthly?
          </div>
        ),
        buttonsContainerClassName: "flex justify-center [&>button]:flex-none",
        buttons: [
          {
            text: "Go Monthly",
            className: "h-10",
            color: "grey",
            onClick: (_, { closeAlert }) => {
              closeAlert();
              setSubscriptionPeriod("month");
            },
          },
          {
            text: "Stay Yearly",
            iconEnd: (
              <ArrowRightIcon className="transition-all group-hover:ml-2" />
            ),
            className:
              "group h-full w-[244px] !rounded-[9px] bg-white !py-0 shadow-[0px_2px_4px_rgba(0,_0,_0,_0.08)] transition-all duration-300 hover:bg-white bg-black text-white hover:bg-black h-10",
            color: "grey",
            role: "close",
          },
        ],
      });
      return;
    }
    setSubscriptionPeriod(period);
  };
  return (
    <>
      <PageLoaderFull isActive={isApplyingPlan} isTransparent />
      <MultiSectionLayout
        classNames={{ content: "bg-grey-100 p-10" }}
        rightSide={
          <SubscriptionSummary
            selected={{}}
            total={summaryTotal}
            basePlan={{
              name: planName,
              details: `${defaultSeats} Seats, ${basePlanPeriodLabel}`,
              price: basePlanPrice,
              breakdown: yearlySummary
                ? `($${yearlySummary.monthlyEquivalentDollars} x 12 months)`
                : `($${basePlanPrice / 100} x 1 month)`,
              originalPrice: yearlySummary?.originalPrice,
            }}
            credits={credits}
            onCompletePurchase={handleCompletePurchase}
            isLoading={isChangePlanInProgress}
            isDisabled={isAlreadyOnSelectedPlan}
            paymentMethod={detailedSubscription?.payment_method}
            onEditPaymentMethod={updateCardDetails}
          />
        }
      >
        <div className="flex w-full justify-center gap-10">
          <div className="flex w-full max-w-[1000px] flex-col gap-4">
            <div className="flex w-full items-center justify-between pb-[10px]">
              <span className="text-dark font-secondary ml-2 text-xl font-medium">
                Selected Plan
              </span>
              {!isSameTierAsCurrent && (
                <div className="flex items-center justify-between gap-2">
                  <span className="bg-gradient-to-r from-[#5F5FE0] to-[#2144A4] bg-clip-text text-[13px] text-transparent">
                    <span className="font-semibold">Save up to 18%</span> with
                    annual billing
                  </span>
                  <div className="flex h-[41px] w-[169px] shrink-0 items-center gap-2 rounded-3xl border-[1px] border-solid border-[#F2F2F2] bg-white p-1 transition-all">
                    <InteractiveContainer
                      className={cx(
                        "text-dark-600 flex h-full w-full items-center justify-center gap-2.5 rounded-[22px] transition-colors",
                        subscriptionPeriod === "year" &&
                          "bg-gradient-to-r from-[#E5EFFF] to-[#E9EBFF] text-black"
                      )}
                      onClick={() => handleIntervalChange("year")}
                    >
                      <span className="font-secondary leading-2 text-sm">
                        Yearly
                      </span>
                    </InteractiveContainer>
                    <InteractiveContainer
                      className={cx(
                        "text-dark-600 flex h-full w-full items-center justify-center rounded-[22px] transition-colors",
                        subscriptionPeriod === "month" &&
                          "bg-gradient-to-r from-[#E5EFFF] to-[#E9EBFF] text-black"
                      )}
                      onClick={() => handleIntervalChange("month")}
                    >
                      <span className="font-secondary leading-2 text-sm">
                        Monthly
                      </span>
                    </InteractiveContainer>
                  </div>
                </div>
              )}
            </div>
            <PlanCardInfo
              plan={targetPlan}
              period={subscriptionPeriod}
              features={selectedPlanFeatures}
              onChangePlan={() => {
                closePurchaseDialog();
                openSubscriptionDrawer();
              }}
            />
          </div>
        </div>
      </MultiSectionLayout>
    </>
  );
};
